import type { Algorithm } from '../data';

interface Props {
  algorithms: Algorithm[];
  onSelect: (id: string) => void;
  onClose: () => void;
}

type AxisKey = 'dataSource' | 'policyMatch' | 'model' | 'method';

const AXES: {
  key: AxisKey;
  title: string;
  question: string;
  options: { value: string; label: string; blurb: string }[];
}[] = [
  {
    key: 'dataSource',
    title: 'Online vs. Offline',
    question: 'Where does the training data come from?',
    options: [
      {
        value: 'online',
        label: 'Online',
        blurb: 'Agent interacts with the env and collects fresh data as it learns. Most classical RL.',
      },
      {
        value: 'offline',
        label: 'Offline',
        blurb: 'Only a fixed dataset is given (no more interaction). Hard because you cannot explore to verify overestimates. Examples: BCQ, CQL, IQL.',
      },
    ],
  },
  {
    key: 'policyMatch',
    title: 'On-policy vs. Off-policy',
    question: 'Is the behavior policy the same as the policy being improved?',
    options: [
      {
        value: 'on-policy',
        label: 'On-policy',
        blurb: 'Learn the value of what you actually do. SARSA, REINFORCE, PPO, A2C.',
      },
      {
        value: 'off-policy',
        label: 'Off-policy',
        blurb: 'Improve a target policy from data generated by a different behavior policy. Q-Learning, DQN, SAC, TD3.',
      },
    ],
  },
  {
    key: 'model',
    title: 'Model-free vs. Model-based',
    question: 'Does the algorithm learn a transition model P(s′|s,a)?',
    options: [
      {
        value: 'model-free',
        label: 'Model-free',
        blurb: 'Skip the model — learn Q or π directly from samples. Most of deep RL.',
      },
      {
        value: 'model-based',
        label: 'Model-based',
        blurb: 'Learn a world model and plan (or generate rollouts) with it. AlphaZero, MuZero, Dreamer.',
      },
    ],
  },
];

/**
 * "What do you parameterize?" gets its own custom-rendered section because the
 * three options each warrant a richer explanation with a concrete analogy.
 */
const METHODS = [
  {
    value: 'value',
    label: 'Value-based',
    oneLine: 'Learn how good each action is. Pick the best one.',
    whatYouBuild: 'A Q-function Q(s, a) — a table or neural net that says "if I take this action in this state, what total reward do I expect?"',
    howYouAct: 'For each action, ask Q. Pick the action with the highest Q-value (plus some ε-greedy randomness for exploration).',
    analogy: 'Like a food critic with a rating for every dish on every menu. To order, look at all the ratings and pick the highest.',
    worksWhenActionsAre: 'discrete (you can enumerate them to find the max)',
    algorithms: ['sarsa', 'q-learning'],
  },
  {
    value: 'policy',
    label: 'Policy-based',
    oneLine: 'Learn how to act directly. Skip the ratings.',
    whatYouBuild: 'A policy π(a | s) — a neural net that takes a state and outputs probabilities over actions (or a mean + variance for continuous).',
    howYouAct: 'Sample an action from π. No argmax over anything.',
    analogy: 'Like a chef who just decides what to cook. No ratings involved — intuition learned over many shifts.',
    worksWhenActionsAre: 'continuous or high-dimensional (no need to max over actions)',
    algorithms: ['reinforce', 'reinforce-baseline', 'grpo'],
  },
  {
    value: 'actor-critic',
    label: 'Actor-Critic',
    oneLine: 'Learn both — a policy that acts, and a value net that coaches it.',
    whatYouBuild: 'TWO networks (or two heads on one body): π_θ (the "actor" — picks actions) and V_ϕ (the "critic" — judges states).',
    howYouAct: 'Act from π. Use V to compute a low-variance advantage signal that tells π which actions were good.',
    analogy: 'A chef (actor) cooking under a food critic (critic) who gives a running score. The chef adjusts based on feedback; the critic calibrates based on what actually sold.',
    worksWhenActionsAre: 'either discrete or continuous — actor-critic is the workhorse of modern RL',
    algorithms: ['a3c', 'trpo', 'ppo'],
  },
];

export default function Overview({ algorithms, onSelect, onClose }: Props) {
  return (
    <div className="max-w-4xl mx-auto px-8 py-8 space-y-10">
      {/* Hero */}
      <div>
        <button
          onClick={onClose}
          className="text-[11px] text-ink-300 hover:text-accent-teal font-mono mb-4"
        >
          ← open the algorithm tree
        </button>
        <h2 className="text-3xl font-bold tracking-tight">
          The <span className="text-accent-teal">4 questions</span> that classify every RL algorithm
        </h2>
        <p className="mt-3 text-ink-200 leading-relaxed max-w-2xl">
          Before looking at specific algorithms, it helps to know the axes the field splits on.
          Every algorithm below has a one-line answer to each of these questions — click any
          algorithm chip to jump straight to it.
        </p>
      </div>

      {/* The four axes */}
      {AXES.map((axis) => (
        <section key={axis.key}>
          <div className="flex items-baseline gap-3 mb-3">
            <h3 className="text-xl font-semibold">{axis.title}</h3>
            <span className="text-xs text-ink-300 font-mono">{axis.question}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {axis.options.map((opt) => {
              const matching = algorithms.filter(
                (a) => a.categories[axis.key] === opt.value
              );
              return (
                <div
                  key={opt.value}
                  className="rounded-lg border border-ink-600 bg-ink-800 p-4"
                >
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="font-semibold text-ink-100">{opt.label}</span>
                    <span className="text-[10px] font-mono text-ink-300">
                      {matching.length} in atlas
                    </span>
                  </div>
                  <p className="text-xs text-ink-200 mb-3 leading-relaxed">{opt.blurb}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {matching.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => onSelect(a.id)}
                        className="text-[11px] px-2 py-1 rounded border border-ink-500 text-ink-100 hover:border-accent-teal hover:text-accent-teal font-mono"
                      >
                        {a.shortName ?? a.name}
                      </button>
                    ))}
                    {matching.length === 0 && (
                      <span className="text-[11px] text-ink-300 italic">
                        (Phase 2 — coming soon)
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {/* The BIG one: what gets parameterized */}
      <section>
        <div className="flex items-baseline gap-3 mb-3">
          <h3 className="text-xl font-semibold">What do you actually build and train?</h3>
          <span className="text-xs text-ink-300 font-mono">
            the single most important distinction
          </span>
        </div>
        <p className="text-sm text-ink-200 leading-relaxed mb-4 max-w-2xl">
          "Parameterize" just means: <em>what function do we fit with a neural network</em>
          (or a table, in the tabular case)? Everything below flows from this choice.
        </p>

        {/* Comparison table */}
        <div className="rounded-lg border border-ink-600 bg-ink-800 overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-600 bg-ink-700/50 text-[10px] uppercase tracking-wider font-mono text-ink-300">
                <th className="text-left px-4 py-2.5">Flavor</th>
                <th className="text-left px-4 py-2.5">What you build</th>
                <th className="text-left px-4 py-2.5">How you act</th>
              </tr>
            </thead>
            <tbody>
              {METHODS.map((m) => (
                <tr key={m.value} className="border-b border-ink-700 last:border-b-0 align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-ink-100">{m.label}</div>
                    <div className="text-[11px] text-ink-300 mt-1">{m.oneLine}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-100 leading-relaxed">
                    {m.whatYouBuild}
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-100 leading-relaxed">
                    {m.howYouAct}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Rich cards per method */}
        <div className="grid md:grid-cols-3 gap-3">
          {METHODS.map((m) => (
            <div key={m.value} className="rounded-lg border border-ink-600 bg-ink-800 p-4 space-y-3">
              <div>
                <div className="font-semibold text-ink-100">{m.label}</div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-ink-300 mt-0.5">
                  analogy
                </div>
                <p className="text-xs text-ink-200 italic mt-1 leading-relaxed">{m.analogy}</p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-ink-300">
                  best when actions are
                </div>
                <p className="text-xs text-ink-100 mt-1">{m.worksWhenActionsAre}</p>
              </div>
              <div className="pt-1">
                <div className="text-[10px] uppercase tracking-widest font-mono text-ink-300 mb-1.5">
                  in this atlas
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {m.algorithms.map((id) => {
                    const a = algorithms.find((al) => al.id === id);
                    if (!a) return null;
                    return (
                      <button
                        key={id}
                        onClick={() => onSelect(id)}
                        className="text-[11px] px-2 py-1 rounded border border-ink-500 text-ink-100 hover:border-accent-teal hover:text-accent-teal font-mono"
                      >
                        {a.shortName ?? a.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Reader's guide */}
      <section className="rounded-lg border border-accent-teal/40 bg-accent-teal/5 p-5">
        <h3 className="text-sm font-semibold text-accent-teal mb-3 font-mono uppercase tracking-widest">
          Suggested reading order
        </h3>
        <ol className="text-sm text-ink-100 space-y-2 list-decimal list-inside">
          <li><strong>Bellman / MDP</strong> — the recursive identity everything tries to solve.</li>
          <li><strong>SARSA → Q-Learning</strong> — the one-character edit that separates on-policy from off-policy.</li>
          <li><strong>REINFORCE → + baseline → A2C</strong> — the evolution from naive to low-variance policy gradient.</li>
          <li><strong>TRPO → PPO → GRPO</strong> — the lineage that powers modern RLHF and LLM post-training.</li>
        </ol>
        <p className="text-xs text-ink-300 mt-3 font-mono">
          Each arrow is a one-line equation diff in the Diff tab.
        </p>
      </section>

      <div className="flex justify-center pt-4">
        <button
          onClick={onClose}
          className="px-5 py-2.5 rounded bg-accent-teal text-ink-900 font-semibold hover:bg-accent-teal/90 text-sm"
        >
          Open the tree →
        </button>
      </div>
    </div>
  );
}
